import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  BookMarked, Link2, FileText, Trash2, Sparkles, Clock, Plus, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { READING_LIST } from "@/constants/testIds";

/**
 * Reading List view — can be rendered standalone (via /reading-list) or as
 * a tab inside the History page. When `embedded=true`, the outer hero
 * header is hidden so it fits naturally under the parent's tab bar.
 */
export function ReadingListView({ embedded = false }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // null | "url" | "text"
  const [urlValue, setUrlValue] = useState("");
  const [textValue, setTextValue] = useState("");
  const [titleValue, setTitleValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reading-list");
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const resetForm = () => {
    setMode(null);
    setUrlValue("");
    setTextValue("");
    setTitleValue("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload =
        mode === "url"
          ? { url: urlValue.trim(), title: titleValue.trim() || undefined }
          : { text: textValue.trim(), title: titleValue.trim() || undefined };
      await api.post("/reading-list", payload);
      toast.success(t("reading_list.added"));
      resetForm();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "error");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t("reading_list.confirm_delete"))) return;
    try {
      await api.delete(`/reading-list/${id}`);
      setItems((cur) => cur.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "error");
    }
  };

  const onSummarize = async (item) => {
    // For text items we need the full body, which the list endpoint doesn't
    // send (to keep the listing light). Fetch the full item first.
    try {
      if (item.kind === "text") {
        const { data } = await api.get(`/reading-list/${item.id}`);
        localStorage.setItem(
          "sux1_reading_list_load",
          JSON.stringify({ kind: "text", text: data.text, title: data.title }),
        );
      } else {
        localStorage.setItem(
          "sux1_reading_list_load",
          JSON.stringify({ kind: "url", url: item.url, title: item.title }),
        );
      }
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "error");
    }
  };

  return (
    <div
      data-testid={READING_LIST.page}
      className={embedded ? "" : "border-t border-border min-h-[calc(100vh-64px)]"}
    >
      {/* Header — only when standalone */}
      {!embedded && (
        <section className="px-6 md:px-10 py-8 border-b border-border">
          <div>
            <div className="sux-marker mb-2">{t("reading_list.page_marker")}</div>
            <h1
              className="text-3xl md:text-4xl font-black tracking-tight leading-tight"
              style={{ fontFamily: "Outfit" }}
            >
              {t("reading_list.page_title")}
            </h1>
            <p className="text-muted-foreground text-sm mt-2 max-w-xl">
              {t("reading_list.page_subtitle")}
            </p>
          </div>
        </section>
      )}

      {/* Add form + list */}
      <section className="px-6 md:px-10 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <p className="text-muted-foreground text-sm max-w-xl">
            {!embedded ? null : t("reading_list.page_subtitle")}
          </p>
          {mode === null && (
            <div className="flex gap-2">
              <button
                onClick={() => setMode("url")}
                data-testid={READING_LIST.addUrlBtn}
                className="font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2"
              >
                <Link2 className="w-3.5 h-3.5" />
                {t("reading_list.add_url")}
              </button>
              <button
                onClick={() => setMode("text")}
                data-testid={READING_LIST.addTextBtn}
                className="font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2.5 border border-[#002FA7] hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
              >
                <FileText className="w-3.5 h-3.5" />
                {t("reading_list.add_text")}
              </button>
            </div>
          )}
        </div>

        {mode !== null && (
          <form
            onSubmit={onSubmit}
            data-testid={READING_LIST.addForm}
            className="mb-8 space-y-4 max-w-3xl"
          >
            <input
              type="text"
              placeholder={t("reading_list.add_title_placeholder")}
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
            />
            {mode === "url" ? (
              <input
                type="url"
                required
                placeholder={t("reading_list.add_url_placeholder")}
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
              />
            ) : (
              <textarea
                required
                minLength={10}
                placeholder={t("reading_list.add_text_placeholder")}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                rows={6}
                className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
              />
            )}
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid={READING_LIST.addFormSubmit}
                className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs px-6 py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Plus className="w-3.5 h-3.5" />
                {submitting ? t("reading_list.adding") : t("reading_list.add_submit")}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-foreground px-3 py-3 flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
        )}
      </section>

      {/* List */}
      <section className="px-6 md:px-10 py-8">
        {loading && <div className="sux-marker">Loading…</div>}

        {!loading && items.length === 0 && (
          <div
            data-testid={READING_LIST.emptyState}
            className="border border-dashed border-border p-12 text-center"
          >
            <BookMarked className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground mb-2">
              {t("reading_list.empty_title")}
            </div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t("reading_list.empty_body")}
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <>
            <div className="sux-marker mb-4">
              {items.length === 1
                ? t("reading_list.count", { count: 1 })
                : t("reading_list.count_plural", { count: items.length })}
            </div>
            <ul className="border border-border divide-y divide-border">
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={READING_LIST.item}
                  className="p-5 flex items-start gap-5 hover:bg-secondary/40 transition-colors flex-wrap md:flex-nowrap"
                >
                  <div className="flex-shrink-0 mt-1">
                    {it.kind === "url" ? (
                      <div className="w-9 h-9 bg-[#002FA7] text-white flex items-center justify-center">
                        <Link2 className="w-4 h-4" />
                      </div>
                    ) : (
                      <div className="w-9 h-9 border border-[#002FA7] flex items-center justify-center">
                        <FileText className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] mb-1 line-clamp-1">
                      {it.title}
                    </div>
                    <div className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {it.text_preview || it.url}
                    </div>
                    <div className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {it.kind === "url"
                          ? t("reading_list.reading_time_url")
                          : t("reading_list.reading_time_short", {
                              minutes: it.reading_minutes,
                              words: it.word_count,
                            })}
                      </span>
                      <span>{new Date(it.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => onSummarize(it)}
                      data-testid={READING_LIST.itemSummarize}
                      className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-[10px] px-4 py-2 hover:bg-[#00227A] transition-colors flex items-center gap-1.5"
                    >
                      <Sparkles className="w-3 h-3" />
                      {t("reading_list.summarize_now")}
                    </button>
                    <button
                      onClick={() => onDelete(it.id)}
                      data-testid={READING_LIST.itemDelete}
                      aria-label={t("reading_list.delete")}
                      className="p-2 text-muted-foreground hover:text-[#002FA7] hover:bg-[#002FA7]/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

// Default export = standalone page used at /reading-list.
export default function ReadingList() {
  return <ReadingListView embedded={false} />;
}
